// src/utils/useNewAppVersion.ts
//
// Je na serveru novější verze aplikace? (fáze 3, I9 – docs/live-faze3-navrh.md
// bod 5.7) Obrazovka rozhodčího po otevření (a po obnovení připojení)
// stáhne index.html bez mezipaměti a porovná jméno hlavního balíčku
// (/static/js/main.<hash>.js) s tím, který běží. Jiné = nová verze →
// upozornění „obnovte“. Nikdy se neobnovuje samo: aplikace nemá service
// worker a na slabém signálu by se stránka nemusela načíst znovu.
//
// Ve vývojovém režimu (bez hashe v názvu balíčku) nic nedělá.

import { useEffect, useRef, useState } from "react";

const MAIN_BUNDLE_RE = /\/static\/js\/main\.[0-9a-f]+\.js/;

/** Hlavní balíček, který právě běží (z <script src>); null = vývojový režim. */
export function runningMainBundle(): string | null {
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  for (const el of scripts) {
    const m = MAIN_BUNDLE_RE.exec(el.getAttribute("src") || "");
    if (m) return m[0];
  }
  return null;
}

export function useNewAppVersion(enabled: boolean): boolean {
  const [isNew, setIsNew] = useState(false);
  useEffect(() => {
    if (!enabled || isNew || typeof fetch !== "function") return undefined;
    const running = runningMainBundle();
    if (!running) return undefined;
    let cancelled = false;
    const check = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      Promise.resolve()
        .then(() => fetch("/index.html", { cache: "no-store" }))
        .then(res => (res && res.ok ? res.text() : ""))
        .then(html => {
          const m = MAIN_BUNDLE_RE.exec(html);
          if (!cancelled && m && m[0] !== running) setIsNew(true);
        })
        .catch(() => {
          // slabý signál – zkusí se po dalším připojení
        });
    };
    check();
    window.addEventListener("online", check);
    return () => {
      cancelled = true;
      window.removeEventListener("online", check);
    };
  }, [enabled, isNew]);
  return isNew;
}

// ── Kiosk: pravidelná kontrola a obnovení (fáze 4a, E3, otázka 12) ─────────
//
// Tabule na TV / počítači s automatickým posuvem (kiosk) se po vydání nové
// verze sama obnoví – ale jen když se nová verze PŘEDEM stáhla (adresa
// stránky, nový index.html, hlavní JS i CSS balíček) a nejvýš jednou za
// 30 minut v této záložce. Zbývající riziko (otázka 12): vypadne-li síť
// mezi stažením a obnovením, zůstane TV na chybové stránce prohlížeče.
// Telefon a rozhodčí se neobnovují nikdy (jen upozornění).

const MAIN_CSS_RE = /\/static\/css\/main\.[0-9a-f]+\.css/;

/** Jak často kiosk kontroluje novou verzi. */
export const KIOSK_VERSION_POLL_MS = 10 * 60 * 1000;
/** Nejmenší odstup dvou automatických obnovení v jedné záložce. */
export const KIOSK_RELOAD_MIN_INTERVAL_MS = 30 * 60 * 1000;
const KIOSK_RELOAD_KEY = "lks_kiosk_reload_at";

/** Smí se teď obnovit? (bez sessionStorage nikdy – nejde hlídat odstup) */
function reloadAllowed(now: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(KIOSK_RELOAD_KEY) || 0);
    return !(now - last < KIOSK_RELOAD_MIN_INTERVAL_MS);
  } catch {
    return false;
  }
}

function markReload(now: number): boolean {
  try {
    sessionStorage.setItem(KIOSK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/**
 * Jedna kontrola kiosku: nová verze → předem stáhnout stránku, JS a CSS →
 * obnovit. Vrací, co se stalo (testy).
 */
export async function kioskVersionCheck(
  running: string,
  reload: () => void,
  now: () => number = Date.now
): Promise<"same" | "reloaded" | "skipped" | "failed"> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "skipped";
  try {
    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res || !res.ok) return "failed";
    const html = await res.text();
    const js = MAIN_BUNDLE_RE.exec(html);
    if (!js || js[0] === running) return "same";
    if (!reloadAllowed(now())) return "skipped";
    const css = MAIN_CSS_RE.exec(html);
    const page = typeof window !== "undefined" ? window.location.href : "/";
    const checks: Array<Promise<boolean>> = [
      fetch(page, { cache: "no-store" }).then((r) => !!r && r.ok, () => false),
      fetch(js[0]).then((r) => !!r && r.ok, () => false),
    ];
    if (css) checks.push(fetch(css[0]).then((r) => !!r && r.ok, () => false));
    const ok = (await Promise.all(checks)).every(Boolean);
    if (!ok) return "failed";
    if (!markReload(now())) return "skipped";
    reload();
    return "reloaded";
  } catch {
    return "failed";
  }
}

/**
 * Kiosk: kontrola nové verze každých 10 min (a po obnovení připojení);
 * `enabled` se čte až v době kontroly (otevřený dialog, vypnutý posuv =
 * neobnovovat). Ve vývojovém režimu nic nedělá.
 */
export function useKioskAutoReload(enabled: boolean): void {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  useEffect(() => {
    if (typeof fetch !== "function") return undefined;
    const running = runningMainBundle();
    if (!running) return undefined;
    let busy = false;
    let cancelled = false;
    const check = () => {
      if (busy || cancelled || !enabledRef.current) return;
      busy = true;
      kioskVersionCheck(running, () => {
        if (!cancelled && enabledRef.current) window.location.reload();
      }).then(() => {
        busy = false;
      });
    };
    const timer = setInterval(check, KIOSK_VERSION_POLL_MS);
    window.addEventListener("online", check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("online", check);
    };
  }, []);
}
